import {
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useState,
} from "react";

import {
  Field as ChakraField,
  InputGroup,
  type InputProps,
  VStack,
} from "@chakra-ui/react";

import { Button } from "@shared/ui/chakra/buttons/button";

import { PasswordInput } from "../../../atoms/password-input";
import { PasswordValidIndicator } from "../../../atoms/password-valid-indicator";
import { Badge } from "../../data-display/badge";

type Props = {
  badgeText?: string;
  defaultValue?: string;
  disabled?: boolean;
  errorText?: string;
  helperText?: string;
  input: InputProps;
  inputBgColor?: string;
  label?: string;
  labelColor?: string;
  labelFontSize?: string;
  labelWeight?: string;
  leftEl?: ReactNode;
  passwordResetLabel?: string;
  placeholder?: string;
  rightEl?: ReactNode;
  visibilityIcon?: { off: React.ReactNode; on: React.ReactNode };
  withIndicator?: boolean;
  isInvalid: boolean;
  isRequired?: boolean;
  onPasswordReset?: () => void;
};

export const PasswordField = ({
  badgeText,
  defaultValue,
  disabled,
  errorText,
  helperText,
  input,
  inputBgColor = "white",
  label,
  labelColor,
  labelFontSize = "sm",
  labelWeight,
  leftEl,
  passwordResetLabel,
  placeholder,
  rightEl,
  visibilityIcon,
  withIndicator,
  isInvalid,
  isRequired,
  onPasswordReset,
}: Props) => {
  const [value, setValue] = useState(defaultValue || "");
  const isPasswordEmpty = value === "";

  const handlePasswordReset: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onPasswordReset?.();
  };
  const { onChange, ...restInput } = input;
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      setValue(e.target.value);
    },
    [onChange],
  );

  return (
    <VStack alignItems="flex-start" gap="4" w="full">
      <ChakraField.Root
        disabled={disabled}
        invalid={isInvalid}
        required={isRequired}
      >
        {label ? (
          <ChakraField.Label
            css={{
              color: labelColor,
              fontSize: labelFontSize,
              fontWeight: labelWeight,
              lineHeight: "1.5",
              transition: "all 0.2s ease",
            }}
          >
            {label}

            {badgeText ? <Badge mx={1}>{badgeText}</Badge> : null}
          </ChakraField.Label>
        ) : null}
        <InputGroup endAddon={rightEl} startAddon={leftEl}>
          <PasswordInput
            _focus={{
              borderColor: "fg.accent",
            }}
            css={{
              backgroundColor: inputBgColor,
              transition: "all 0.2s ease",
            }}
            focusVisibleRing={"none"}
            placeholder={placeholder}
            visibilityIcon={visibilityIcon}
            onChange={handleChange}
            {...restInput}
          />
        </InputGroup>
        {isInvalid && errorText && (
          <ChakraField.ErrorText>{errorText} &nbsp;</ChakraField.ErrorText>
        )}
        {!isInvalid && helperText && (
          <ChakraField.HelperText>{helperText} &nbsp;</ChakraField.HelperText>
        )}
      </ChakraField.Root>
      {passwordResetLabel && onPasswordReset && (
        <Button variant="empty-link" onClick={handlePasswordReset}>
          {passwordResetLabel}
        </Button>
      )}
      {withIndicator && (
        <PasswordValidIndicator
          value={value}
          isPasswordEmpty={isPasswordEmpty}
        />
      )}
    </VStack>
  );
};
